import React, { useCallback, useEffect, useState } from "react";
import { Search, User, ChevronDown, UserPlus } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Input } from "../ui/input";
import { apiClient } from "../../lib/apiClient";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { cn } from "../../lib/utils";

export function CustomerPicker({ value, label, onSelect, onAddNew, disabled, testId = "customer-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/customers", {
        params: { q: debouncedQuery || undefined, limit: 8 },
      });
      setResults(data.items);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    if (open) search();
  }, [open, search]);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text-main transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-70"
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            <User size={14} strokeWidth={1.5} className="text-text-muted" />
            {label || (value ? `Customer #${value}` : "Select a customer...")}
          </span>
          {!disabled && <ChevronDown size={14} strokeWidth={1.5} className="text-text-muted" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start" data-testid={`${testId}-content`}>
        {onAddNew && (
          <button
            type="button"
            onClick={() => { setOpen(false); onAddNew(); }}
            className="mb-2 flex w-full items-center gap-2 rounded-md border border-dashed border-accent/50 px-2 py-2 text-sm font-medium text-accent-hover transition-colors hover:bg-accent/10"
            data-testid={`${testId}-add-new`}
          >
            <UserPlus size={15} strokeWidth={1.75} /> Add New Customer
          </button>
        )}
        <div className="relative mb-2">
          <Search size={14} strokeWidth={1.5} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            autoFocus
            className="h-8 pl-8 text-sm"
            placeholder="Search customers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid={`${testId}-search-input`}
          />
        </div>
        <div className="max-h-60 space-y-0.5 overflow-y-auto">
          {loading ? (
            <p className="px-2 py-3 text-center text-xs text-text-muted">Searching...</p>
          ) : results.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-text-muted">No customers found.</p>
          ) : (
            results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onSelect(c.id, `${c.firstname || ""} ${c.lastname || ""}`.trim());
                  setOpen(false);
                }}
                className="flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm hover:bg-background"
                data-testid={`${testId}-option-${c.id}`}
              >
                <span className="font-medium text-text-main">{`${c.firstname || ""} ${c.lastname || ""}`.trim() || `#${c.id}`}</span>
                <span className="text-xs text-text-muted">{c.phone || c.email || `Customer #${c.id}`}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
